import { FC, useState, useMemo, useEffect, useRef } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { usePayers } from "../../hooks/userPayers";
import {
	useCreatePayer,
	useUpdatePayer,
	useDeletePayer,
} from "../../hooks/userPayers/useMutatePayer";
import { Card, CardContent } from "@mui/material";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import { PayerResponse, CreatePayer } from "@/domain/payer.interface";
import PayerDialog from "../../components/Payers/PayerDialog";
import DeleteConfirmDialog from "../../components/Payers/DeleteConfirmDialog";
import { useUserStore } from "../../stores/user.store";
import { useFeedBack } from "../../providers/FeedBackProvider/FeedBackContext";
import { useCreateAuditlog } from "../../hooks/useAuditlog";
import AuditLogModal from "../../components/modals/AuditLog";
import { useAppTheme } from "../../utils/theme";

interface ApiError {
	response?: {
		data?: {
			message?: string | string[];
		};
	};
	message?: string;
}

const PayersComponent: FC = () => {
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [selectedPayer, setSelectedPayer] = useState<PayerResponse | null>(
		null,
	);
	const [openAuditLogModal, setOpenAuditLogModal] = useState(false);

	const { userInfo } = useUserStore();
	const { showSnackBar } = useFeedBack();
	const theme = useAppTheme();
	const createAuditlogMutation = useCreateAuditlog();
	const postAuditlogCalledRef = useRef(false);

	const {
		data: PayersResponse,
		isLoading: payersLoading,
		error: payersError,
	} = usePayers();

	const createMutation = useCreatePayer();
	const updateMutation = useUpdatePayer();
	const deleteMutation = useDeletePayer();

	const postAuditlog = async ({
		action,
		actionDetail,
		actionCode,
	}: {
		action: string;
		actionDetail: string;
		actionCode: string;
	}) => {
		await createAuditlogMutation.mutateAsync({
			appType: "WEB_BACK_OFFICE",
			module: "Payers",
			action,
			detail: actionDetail,
			actionCode,
			option: "Payers Management",
		});
	};

	useEffect(() => {
		if (!postAuditlogCalledRef.current) {
			postAuditlog({
				action: "When entering",
				actionDetail: "Viewed the payers management page",
				actionCode: "WEB_PAYER_LIST_VIEW",
			});
			postAuditlogCalledRef.current = true;
		}
	}, []);

	const payers = PayersResponse || [];

	useEffect(() => {
		if (search) {
			postAuditlog({
				action: "When searching",
				actionDetail: "Searched within payers list",
				actionCode: "WEB_PAYER_LIST_SEARCH",
			});
		}
	}, [search]);

	// Filter payers based on search
	const filteredPayers = useMemo(() => {
		if (!search) return payers;
		const searchLower = search.toLowerCase();
		return payers.filter(
			(payer) =>
				payer.name.toLowerCase().includes(searchLower) ||
				payer.description.toLowerCase().includes(searchLower) ||
				payer.regularExpression.toLowerCase().includes(searchLower),
		);
	}, [payers, search]);

	// Paginate filtered payers
	const paginatedPayers = useMemo(() => {
		const startIndex = page * pageSize;
		return filteredPayers.slice(startIndex, startIndex + pageSize);
	}, [filteredPayers, page, pageSize]);

	const handleOpenDialog = (payer?: PayerResponse) => {
		setSelectedPayer(payer || null);
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setDialogOpen(false);
		setSelectedPayer(null);
	};

	const handleOpenDeleteDialog = (payer: PayerResponse) => {
		setSelectedPayer(payer);
		setDeleteDialogOpen(true);
	};

	const handleCloseDeleteDialog = () => {
		setDeleteDialogOpen(false);
		setSelectedPayer(null);
	};

	const handleSubmit = async (data: CreatePayer) => {
		try {
			if (selectedPayer) {
				await updateMutation.mutateAsync({
					...data,
					_id: selectedPayer._id,
					updatedBy: userInfo?.id || "",
				});
				await postAuditlog({
					action: "When updating",
					actionDetail: `Updated payer: ${data.name}`,
					actionCode: "WEB_PAYER_UPD",
				});
				showSnackBar("Payer updated successfully", "success");
			} else {
				await createMutation.mutateAsync({
					...data,
					createdBy: userInfo?.id || "",
				});
				await postAuditlog({
					action: "When creating",
					actionDetail: `Created new payer: ${data.name}`,
					actionCode: "WEB_PAYER_ADD",
				});
				showSnackBar("Payer created successfully", "success");
			}
			handleCloseDialog();
		} catch (error: unknown) {
			const apiError = error as ApiError;
			const errorMessage =
				apiError?.response?.data?.message ||
				apiError?.message ||
				`Failed to ${selectedPayer ? "update" : "create"} payer`;
			const message = Array.isArray(errorMessage)
				? errorMessage.join(", ")
				: errorMessage;
			showSnackBar(message, "error");
		}
	};

	const handleDelete = async () => {
		if (!selectedPayer) return;

		try {
			await deleteMutation.mutateAsync({
				_id: selectedPayer._id,
				updatedBy: userInfo?.id || "",
			});
			await postAuditlog({
				action: "When deleting",
				actionDetail: `Deleted payer: ${selectedPayer.name}`,
				actionCode: "WEB_PAYER_DELETE",
			});
			showSnackBar("Payer deleted successfully", "success");
			handleCloseDeleteDialog();
		} catch (error: unknown) {
			const apiError = error as ApiError;
			const errorMessage =
				apiError?.response?.data?.message ||
				apiError?.message ||
				"Failed to delete payer";
			const message = Array.isArray(errorMessage)
				? errorMessage.join(", ")
				: errorMessage;
			showSnackBar(message, "error");
		}
	};

	if (payersLoading) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				minHeight="400px"
			>
				<Typography>Loading payers...</Typography>
			</Box>
		);
	}

	if (payersError) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				minHeight="400px"
			>
				<Typography color="error">
					Error loading payers: {payersError?.message}
				</Typography>
			</Box>
		);
	}

	return (
		<Box>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				mb={3}
			>
				<Typography variant="h4">Payers Management</Typography>
				<Box display="flex" gap={2}>
					<Tooltip title="Audit log">
						<IconButton
							size="large"
							sx={{
								borderRadius: "8px",
								border: `1px solid ${theme.palette.outline.dark}`,
							}}
							onClick={() => setOpenAuditLogModal(true)}
						>
							<HistoryOutlinedIcon className="text-icon-primary" />
						</IconButton>
					</Tooltip>
					<Button
						variant="contained"
						color="primary"
						startIcon={<AddIcon />}
						onClick={() => handleOpenDialog()}
					>
						Add Payer
					</Button>
				</Box>
			</Box>

			<Card>
				<CardContent>
					<Box mb={2}>
						<TextField
							fullWidth
							placeholder="Search payers..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon />
									</InputAdornment>
								),
							}}
						/>
					</Box>

					<TableContainer component={Paper}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Name</TableCell>
									<TableCell>Description</TableCell>
									<TableCell>Regular Expression</TableCell>
									<TableCell>Autogenerated</TableCell>
									<TableCell>Status</TableCell>
									<TableCell align="center">Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{paginatedPayers.length === 0 ? (
									<TableRow>
										<TableCell colSpan={6} align="center">
											<Typography color="textSecondary">
												No payers found
											</Typography>
										</TableCell>
									</TableRow>
								) : (
									paginatedPayers.map((payer) => (
										<TableRow key={payer._id}>
											<TableCell>{payer.name}</TableCell>
											<TableCell>{payer.description}</TableCell>
											<TableCell>
												<code style={{ fontSize: "0.875rem" }}>
													{payer.regularExpression}
												</code>
											</TableCell>
											<TableCell>
												<Chip
													label={payer.autogenerated === "Y" ? "Yes" : "No"}
													size="small"
													color={
														payer.autogenerated === "Y" ? "primary" : "default"
													}
												/>
											</TableCell>
											<TableCell>
												<Chip
													label={payer.status}
													size="small"
													color={
														payer.status === "ACTIVE" ? "success" : "default"
													}
												/>
											</TableCell>
											<TableCell align="center">
												<IconButton
													size="small"
													color="primary"
													onClick={() => handleOpenDialog(payer)}
													title="Edit"
												>
													<EditIcon />
												</IconButton>
												<IconButton
													size="small"
													color="error"
													onClick={() => handleOpenDeleteDialog(payer)}
													title="Delete"
												>
													<DeleteIcon />
												</IconButton>
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>

					<TablePagination
						component="div"
						count={filteredPayers.length}
						page={page}
						onPageChange={(_, newPage) => setPage(newPage)}
						rowsPerPage={pageSize}
						onRowsPerPageChange={(event) => {
							setPageSize(parseInt(event.target.value, 10));
							setPage(0);
						}}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				</CardContent>
			</Card>

			<PayerDialog
				open={dialogOpen}
				onClose={handleCloseDialog}
				onSubmit={handleSubmit}
				initialData={selectedPayer}
				isLoading={createMutation.isPending || updateMutation.isPending}
			/>

			<DeleteConfirmDialog
				open={deleteDialogOpen}
				onClose={handleCloseDeleteDialog}
				onConfirm={handleDelete}
				payerName={selectedPayer?.name}
				isLoading={deleteMutation.isPending}
			/>

			{openAuditLogModal ? (
				<AuditLogModal
					open={openAuditLogModal}
					module="Payers"
					onClose={() => setOpenAuditLogModal(false)}
				/>
			) : null}
		</Box>
	);
};

export default PayersComponent;
